import { useEffect, useMemo, useRef } from "react";
import { midpoint } from "@ui/lib/linear_interpolation";

export function useAnnotationBehavior({
  index,
  position,
  anchorRelativePositions,
  anchor,
  updateAnnotationAnchorPosition,
  updateAnnotationRelativePosition,
  selectAnnotation,
}: {
  index: number;
  position: [number, number, number, number];
  anchorRelativePositions: [number, number];
  anchor: { x: "left" | "center" | "right"; y: "top" | "center" | "bottom" };
  updateAnnotationAnchorPosition?: (
    position: [number, number, number, number],
    index: number,
  ) => void;
  updateAnnotationRelativePosition?: (position: [number, number], index: number) => void;
  selectAnnotation?: (index: number) => void;
}) {
  const annotationRef = useRef<HTMLDivElement | null>(null);
  const annotationDataRef = useRef<HTMLDivElement | null>(null);
  const annotationResizerRef = useRef<HTMLDivElement | null>(null);
  const annotationHasArea = useMemo(() => position[2] > 0 && position[3] > 0, [position]);
  const labelOnly = useMemo(() => position[2] === -1 && position[3] === -1, [position]);

  useEffect(() => {
    if (!annotationRef.current) return;

    const $annotation = annotationRef.current;
    const $annotationData = annotationRef.current.children[0] as HTMLElement;
    const $resizer = annotationResizerRef.current;
    const $overlay = $annotation.parentElement;

    if (!$overlay || !$annotationData) return;

    let overlayWidth = $overlay.clientWidth;
    let overlayHeight = $overlay.clientHeight;
    const resizeObserver = new ResizeObserver(() => {
      overlayWidth = $overlay.clientWidth;
      overlayHeight = $overlay.clientHeight;
    });
    resizeObserver.observe($overlay);

    // will be the normalized ratio(0-1) with respect to the overlay width
    let annotationXRatio: number = position[0] ?? 0;
    let annotationYRatio: number = position[1] ?? 0;

    let annotationWidth: number = position[2] ?? 0;
    let annotationHeight: number = position[3] ?? 0;
    let [annotationDataXRatio, annotationDataYRatio] = anchorRelativeInitialPositions(
      anchorRelativePositions,
      anchor,
    );

    applyAnnotationPosition($annotation, annotationXRatio, annotationYRatio);
    if (annotationHasArea) applyAnnotationSize($annotation, annotationWidth, annotationHeight);
    applyAnnotationDataPosition($annotationData, annotationDataXRatio, annotationDataYRatio);

    // Update anchor
    $annotation.addEventListener("pointerdown", dragAnnotation, { passive: true });
    function dragAnnotation(e: PointerEvent) {
      e.stopPropagation();
      selectAnnotation?.(index);

      if (!updateAnnotationAnchorPosition) return;

      const ctrl = new AbortController();

      const overlayRect = $overlay?.getBoundingClientRect();
      const left = overlayRect?.left ?? 0;
      const top = overlayRect?.top ?? 0;
      const width = overlayRect?.width ?? 1;
      const height = overlayRect?.height ?? 1;

      const annotationStartX = e.clientX - annotationXRatio * width - left;
      const annotationStartY = e.clientY - annotationYRatio * height - top;

      $overlay?.classList.add("dragging");
      $annotationData.classList.add("dragging");
      $annotation.style.cursor = "grabbing";
      window?.addEventListener("pointerup", stop, { passive: true, once: true });
      function stop() {
        $overlay?.classList.remove("dragging");
        updateAnnotationAnchorPosition?.(
          [annotationXRatio, annotationYRatio, annotationWidth, annotationHeight],
          index,
        );
        $annotation.style.cursor = "grab";
        $annotationData.classList.remove("dragging");
        ctrl.abort();
      }

      window.addEventListener("pointermove", move, { signal: ctrl.signal });
      function move(e: PointerEvent) {
        e.stopPropagation();
        const left = overlayRect?.left ?? 0;
        const top = overlayRect?.top ?? 0;
        const width = overlayRect?.width ?? 1;
        const height = overlayRect?.height ?? 1;
        annotationXRatio = (e.clientX - annotationStartX - left) / width;
        annotationYRatio = (e.clientY - annotationStartY - top) / height;
      }
    }
    const dragAnnotationObject = (e: PointerEvent) => {
      ($annotation.classList.contains("expanded") ? dragAnnotationData : dragAnnotation)(e);
    };
    // Update annotation annotationData
    if (!labelOnly) {
      $annotationData.addEventListener("pointerdown", dragAnnotationObject, { passive: true });
    }
    function dragAnnotationData(e: PointerEvent) {
      e.stopPropagation();
      selectAnnotation?.(index);

      if (!updateAnnotationRelativePosition) return;

      const ctrl = new AbortController();
      const overlayScale = getOverlayImageScale($overlay);

      const annotationDataStartX = e.clientX - annotationDataXRatio * overlayScale;
      const annotationDataStartY = e.clientY - annotationDataYRatio * overlayScale;

      $annotationData.style.cursor = "grabbing";
      $overlay?.classList.add("dragging");

      window?.addEventListener("pointerup", stop, { passive: true, once: true });
      function stop() {
        $overlay?.classList.remove("dragging");
        $annotationData.style.cursor = "grab";
        updateAnnotationRelativePosition?.([annotationDataXRatio, annotationDataYRatio], index);
        $annotationData.classList.remove("dragging");
        ctrl.abort();
      }

      window.addEventListener("pointermove", move, { signal: ctrl.signal });
      function move(e: PointerEvent) {
        e.stopPropagation();
        const overlayScale = getOverlayImageScale($overlay);
        annotationDataXRatio = (e.clientX - annotationDataStartX) / overlayScale;
        annotationDataYRatio = (e.clientY - annotationDataStartY) / overlayScale;
      }
    }

    // Resize
    $resizer?.addEventListener("pointerdown", resizeAnnotation, { passive: true });
    function resizeAnnotation(e: PointerEvent) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      selectAnnotation?.(index);

      const ctrl = new AbortController();

      let startX = e.clientX;
      let startY = e.clientY;

      $overlay?.classList.add("resizing");
      $overlay?.addEventListener("pointerup", stop, { passive: true, once: true });
      function stop() {
        updateAnnotationAnchorPosition?.(
          [annotationXRatio, annotationYRatio, annotationWidth, annotationHeight],
          index,
        );
        $overlay?.classList.remove("resizing");
        ctrl.abort();
      }

      $overlay?.addEventListener("pointermove", move, { signal: ctrl.signal });
      function move(e: PointerEvent) {
        e.stopPropagation();
        annotationWidth += (e.clientX - startX) / overlayWidth;
        annotationHeight += (e.clientY - startY) / overlayHeight;
        startX = e.clientX;
        startY = e.clientY;
      }
    }

    // Update
    let frame: number | null = null;
    let nx: number = annotationXRatio;
    let ny: number = annotationYRatio;
    let nw: number = annotationWidth;
    let nh: number = annotationHeight;
    let ndx: number = annotationDataXRatio;
    let ndy: number = annotationDataYRatio;
    function render() {
      frame = window.requestAnimationFrame(render);
      if (annotationXRatio === undefined || annotationYRatio === undefined) return;

      nx = midpoint(annotationXRatio, nx);
      ny = midpoint(annotationYRatio, ny);
      nw = midpoint(annotationWidth, nw);
      nh = midpoint(annotationHeight, nh);
      ndx = midpoint(annotationDataXRatio, ndx);
      ndy = midpoint(annotationDataYRatio, ndy);

      applyAnnotationPosition($annotation, nx, ny);
      if (annotationHasArea) applyAnnotationSize($annotation, nw, nh);
      applyAnnotationDataPosition($annotationData, ndx, ndy);

      const overlayScale = getOverlayImageScale($overlay);
      const scaledAnnotationDataX = ndx * overlayScale;
      const scaledAnnotationDataY = ndy * overlayScale;
      const theta = Math.atan2(ndy, ndx);
      const [ix, iy] = rectangleSegmentIntersectionFromAngle(
        theta,
        nw * overlayWidth,
        nh * overlayHeight,
      );
      const length =
        Math.sqrt(
          scaledAnnotationDataX * scaledAnnotationDataX +
            scaledAnnotationDataY * scaledAnnotationDataY,
        ) - Math.sqrt(ix * ix + iy * iy);
      applyStrokeValues($annotation, ix, iy, length, theta * (180 / Math.PI));
    }
    frame = window.requestAnimationFrame(render);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      $annotation.removeEventListener("pointerdown", dragAnnotation);
      if (!labelOnly) {
        $annotationData.removeEventListener("pointerdown", dragAnnotationObject);
      }
      $resizer?.removeEventListener("pointerdown", resizeAnnotation);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    annotationHasArea,
    index,
    position,
    anchorRelativePositions,
    selectAnnotation,
    labelOnly,
    anchor.x,
    anchor.y,
  ]);

  return {
    annotationRef,
    annotationDataRef,
    annotationResizerRef,
    area: annotationHasArea,
    labelOnly,
  };
}

function applyAnnotationPosition(element: HTMLElement, x: number, y: number) {
  element.style.setProperty("--annotation-x", String(x));
  element.style.setProperty("--annotation-y", String(y));
}

function applyAnnotationSize(element: HTMLElement, w: number, h: number) {
  element.style.setProperty("--annotation-w", String(w));
  element.style.setProperty("--annotation-h", String(h));
}

function applyAnnotationDataPosition(element: HTMLElement, x: number, y: number) {
  element.style.setProperty("--annotation-data-x", String(x));
  element.style.setProperty("--annotation-data-y", String(y));
}

function getOverlayImageScale(overlay: HTMLElement | null) {
  if (!overlay) return 1;

  const scale = Number.parseFloat(
    window.getComputedStyle(overlay).getPropertyValue("--image-viewer-scale"),
  );

  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function applyStrokeValues(
  element: HTMLElement,
  x: number,
  y: number,
  length: number,
  rotation: number, // deg
) {
  element.style.setProperty("--annotation-stroke-x", String(x));
  element.style.setProperty("--annotation-stroke-y", String(y));
  element.style.setProperty("--annotation-stroke-length", String(length));
  element.style.setProperty("--annotation-stroke-rotation", String(rotation));
}

/** @description Currently with anchor fallback */
const DEFAULT_DISTANCE = 100;
function anchorRelativeInitialPositions(
  anchorRelativePositions: [number, number],
  anchor: { x: "left" | "center" | "right"; y: "top" | "center" | "bottom" },
): [number, number] {
  const [relativeX, relativeY] = anchorRelativePositions ?? [];
  const defaultX =
    anchor.x === "center" ? 0 : anchor.x === "left" ? -DEFAULT_DISTANCE : DEFAULT_DISTANCE;
  const defaultY =
    anchor.y === "center" ? 0 : anchor.y === "top" ? -DEFAULT_DISTANCE : DEFAULT_DISTANCE;
  return [relativeX ?? defaultX, relativeY ?? defaultY];
}

function rectangleSegmentIntersectionFromAngle(
  rad: number,
  width: number,
  height: number,
): [number, number] {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  if (width * Math.abs(s) < height * Math.abs(c)) {
    const x = (Math.sign(c) * width) / 2;
    const y = Math.tan(rad) * x;
    return [x, y];
  } else {
    const y = (Math.sign(s) * height) / 2;
    const x = (1 / Math.tan(rad)) * y;
    return [x, y];
  }
}
